import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Thermometer, TriangleAlert, Zap, Wrench } from "lucide-react";
import { Reveal, Eyebrow, SlideShell, SectionHead, EASE } from "./shared";

const MaskedWords = ({ text, isPrint, className = "", baseDelay = 0.2 }) => {
  if (isPrint) return <span className={className}>{text}</span>;
  const words = text.split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom pb-2 -mb-2">
          <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: baseDelay + i * 0.05, ease: EASE }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

const MARQUEE_ITEMS = [
  "Progettazione",
  "Realizzazione",
  "Manutenzione",
  "Efficienza energetica",
  "Continuità di servizio",
  "Comfort",
];

const Marquee = () => (
  <div className="absolute bottom-0 inset-x-0 border-t border-white/10 py-4 overflow-hidden" data-testid="cover-marquee">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex items-center shrink-0">
          {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((t, i) => (
            <span key={i} className="flex items-center">
              <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-white/40 px-6 whitespace-nowrap">
                {t}
              </span>
              <span className="w-1.5 h-1.5 bg-brand shrink-0" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const CoverSlide = ({ slide, isPrint, onNext }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(useTransform(mx, [0, 1], [16, -16]), { stiffness: 50, damping: 20 });
  const sy = useSpring(useTransform(my, [0, 1], [10, -10]), { stiffness: 50, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div
      className="relative h-[100dvh] w-full overflow-hidden bg-graphite"
      onMouseMove={isPrint ? undefined : onMouseMove}
      data-testid="slide-cover"
    >
      {slide.image &&
        (isPrint ? (
          <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50 scale-105" />
        ) : (
          <motion.img
            src={slide.image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-50"
            style={{ x: sx, y: sy, scale: 1.08 }}
          />
        ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#090A0C] via-[#090A0C]/50 to-[#090A0C]/10" />

      <Reveal isPrint={isPrint} className="absolute top-20 sm:top-24 left-6 sm:left-12 lg:left-20 xl:left-28 z-10">
        <img
          src="/logo.png"
          alt="Proaura"
          data-testid="cover-logo"
          className="h-12 sm:h-16 w-auto [filter:drop-shadow(0_0_16px_rgba(255,255,255,0.9))_drop-shadow(0_2px_6px_rgba(255,255,255,0.55))]"
        />
      </Reveal>

      <div className="relative h-full flex flex-col justify-end px-6 sm:px-12 lg:px-20 xl:px-28 pb-36">
        <div className="max-w-7xl mx-auto w-full">
          <Reveal isPrint={isPrint} delay={0.1}>
            <Eyebrow dark>{slide.eyebrow}</Eyebrow>
          </Reveal>
          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] max-w-4xl">
            <MaskedWords text={slide.title} isPrint={isPrint} />
          </h1>
          <Reveal isPrint={isPrint} delay={0.7}>
            <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl">{slide.subtitle}</p>
          </Reveal>
          {!isPrint && slide.cta && (
            <Reveal delay={0.85}>
              <button
                data-testid="cover-cta-button"
                onClick={(e) => {
                  e.currentTarget.blur();
                  onNext && onNext();
                }}
                className="group mt-9 inline-flex items-center gap-3 bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm uppercase tracking-widest px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                {slide.cta}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          )}
        </div>
      </div>
      {!isPrint && <Marquee />}
    </div>
  );
};

export const SplitSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-white">
    <div data-testid="slide-about" className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal isPrint={isPrint} className="relative order-2 lg:order-1">
        <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-brand pointer-events-none" />
        {slide.image && (
          <img
            src={slide.image}
            alt=""
            className="w-full h-64 sm:h-80 lg:h-[26rem] object-cover"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}
          />
        )}
        <div className="absolute -bottom-4 -right-4 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
          PROAURA / IMP.01
        </div>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal isPrint={isPrint} delay={0.05}>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
        </Reveal>
        <Reveal isPrint={isPrint} delay={0.12}>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-ink">
            {slide.title}
          </h2>
        </Reveal>
        <Reveal isPrint={isPrint} delay={0.2}>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{slide.subtitle}</p>
        </Reveal>
        <div className="mt-8">
          {(slide.items || []).map((it, i) => (
            <Reveal key={i} isPrint={isPrint} delay={0.28 + i * 0.08} className="flex gap-5 border-t border-line py-4">
              <span className="font-mono text-xs text-brand pt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div className="font-display font-semibold text-ink">{it.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{it.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </SlideShell>
);

const PROBLEM_ICONS = [Thermometer, TriangleAlert, Zap, Wrench];

export const CardsSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-mist bg-blueprint-light">
    <div data-testid="slide-problem">
      <SectionHead slide={slide} isPrint={isPrint} />
      <div className="mt-10 grid sm:grid-cols-2 gap-4 lg:gap-5">
        {(slide.items || []).map((it, i) => {
          const Icon = PROBLEM_ICONS[i % PROBLEM_ICONS.length];
          return (
            <Reveal key={i} isPrint={isPrint} delay={0.15 + i * 0.08}>
              <div className="group relative h-full bg-white border border-line rounded-xl p-6 lg:p-7 overflow-hidden transition-colors duration-300 hover:border-brand/60">
                <span className="absolute top-0 left-0 h-[3px] w-full bg-brand origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" />
                <div className="flex items-start justify-between">
                  <span className="inline-flex p-2.5 rounded-lg bg-brand-subtle text-brand">
                    <Icon size={20} strokeWidth={1.8} />
                  </span>
                  <span className="font-mono text-xs text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-5 font-display font-bold text-xl text-ink tracking-tight">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{it.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </SlideShell>
);

export const PillarsSlide = ({ slide, isPrint }) => (
  <SlideShell className="bg-white">
    <div data-testid="slide-solution">
      <SectionHead slide={slide} isPrint={isPrint} />
      <div className="mt-10 grid md:grid-cols-3 gap-4 lg:gap-5">
        {(slide.items || []).map((it, i) => (
          <Reveal key={i} isPrint={isPrint} delay={0.15 + i * 0.1} className="h-full">
            <div className="h-full bg-mist border border-line rounded-xl p-7 lg:p-8 flex flex-col transition-colors duration-300 hover:bg-brand-subtle/40">
              <span className="font-mono font-bold text-3xl text-brand">{String(i + 1).padStart(2, "0")}</span>
              <span className="mt-4 h-px w-full bg-line" />
              <h3 className="mt-5 font-display font-bold text-xl lg:text-2xl text-ink tracking-tight">{it.title}</h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{it.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);
